import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit2, Plus, Save, Upload, X } from 'lucide-react';
import Select from 'react-select';
import {
  add_manufacturing_comment,
  add_manufacturing_product,
  add_manufacturing_serial,
  add_manufacturing_serials_bulk,
  change_manufacturing_status,
  delete_manufacturing_file,
  delete_manufacturing_product,
  delete_manufacturing_serial,
  get_manufacturing_order,
  list_manufacturing_catalog_products,
  manufacturingFileUrl,
  MANUFACTURING_PRIORITY_LABELS,
  parseManufacturingSerialFile,
  update_manufacturing_order,
  update_manufacturing_product,
  upload_manufacturing_content_image,
  upload_manufacturing_file,
} from '../../../utils/manufacturing_services';
import { prepareTaskDescriptionHtmlForSave } from '../../../utils/task_description_html';
import TaskDescriptionEditor from '../talento/TaskDescriptionEditor';
import TaskDescriptionView from '../talento/TaskDescriptionView';
import StrategyCommentComposer from '../talento/StrategyCommentComposer';
import UserAvatar from '../dashboard/UserAvatar';
import ManufacturingStatusBar from './ManufacturingStatusBar';
import '../crm/opportunity-header.css';
import '../talento/TaskDescriptionEditor.css';
import '../talento/StrategyCommentComposer.css';
import './ProduccionDetail.css';

function productLineRef(prod) {
  if (prod?.source === 'manual') {
    return { manufacturing_product_id: prod.id };
  }
  return { opportunity_product_id: prod.id };
}

function serialGroupKey(serialRow) {
  const mfgLine = Number(serialRow?.manufacturing_product_id || 0);
  if (mfgLine > 0) return String(mfgLine);
  return String(serialRow?.opportunity_product_id || '');
}

function getQueryId() {
  const params = new URLSearchParams(window.location.search);
  return parseInt(params.get('id'), 10) || 0;
}

function formatCommentDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr);
  return d.toLocaleString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function CommentBody({ text }) {
  const raw = text == null ? '' : String(text);
  if (!raw.trim()) return null;
  const looksHtml = /<[a-z][\s\S]*>/i.test(raw);
  if (looksHtml) {
    return <TaskDescriptionView html={raw} className="task-description-view mfg-comment-body" />;
  }
  return <div className="mfg-comment-plain">{raw}</div>;
}

export default function ProduccionDetail({ usr_id, usr_name, permission }) {
  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  const isAdmin = String(permission) === '1';
  const manufacturingId = getQueryId();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [products, setProducts] = useState([]);
  const [serials, setSerials] = useState([]);
  const [comments, setComments] = useState([]);
  const [files, setFiles] = useState([]);

  const [priority, setPriority] = useState(2);
  const [expectedDate, setExpectedDate] = useState('');
  const [savingFields, setSavingFields] = useState(false);

  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [savingDescription, setSavingDescription] = useState(false);

  const [editingContent, setEditingContent] = useState(false);
  const [contentDraft, setContentDraft] = useState('');
  const [savingContent, setSavingContent] = useState(false);

  const [commentDraft, setCommentDraft] = useState('');
  const [commentKey, setCommentKey] = useState(0);
  const [submittingComment, setSubmittingComment] = useState(false);

  const [serialDrafts, setSerialDrafts] = useState({});
  const [busySerial, setBusySerial] = useState(null);
  const serialFileInputRef = useRef(null);
  const [serialUploadProduct, setSerialUploadProduct] = useState(null);
  const [serialParseResult, setSerialParseResult] = useState(null);
  const [serialUploadFileName, setSerialUploadFileName] = useState('');
  const [serialUploading, setSerialUploading] = useState(false);

  const [statusModal, setStatusModal] = useState(null); // { status, field: 'factura'|'guia' }
  const [statusFieldValue, setStatusFieldValue] = useState('');
  const [statusWarrantyDate, setStatusWarrantyDate] = useState('');
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusFieldError, setStatusFieldError] = useState('');
  const [statusWarrantyError, setStatusWarrantyError] = useState('');

  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);

  const [catalogOptions, setCatalogOptions] = useState([]);
  const [selectedCatalogProduct, setSelectedCatalogProduct] = useState(null);
  const [newProductUnits, setNewProductUnits] = useState('1');
  const [addingProduct, setAddingProduct] = useState(false);
  const [busyProductLine, setBusyProductLine] = useState(null);
  const [unitsDrafts, setUnitsDrafts] = useState({});

  const loadOrder = useCallback(async () => {
    if (!manufacturingId) return;
    setLoading(true);
    const result = await get_manufacturing_order(token, manufacturingId);
    if (result === 'INVALID') {
      navigate('/');
      return;
    }
    if (!result || result.ok !== true || !result.order) {
      setOrder(null);
      setLoading(false);
      return;
    }
    setOrder(result.order);
    setProducts(Array.isArray(result.products) ? result.products : []);
    setSerials(Array.isArray(result.serials) ? result.serials : []);
    setComments(Array.isArray(result.comments) ? result.comments : []);
    setFiles(Array.isArray(result.files) ? result.files : []);
    setDescriptionDraft(result.order.description || '');
    setPriority(Number(result.order.priority) || 2);
    const exp = result.order.expected_finishing_date;
    setExpectedDate(!exp || exp === '0000-00-00' ? '' : String(exp).slice(0, 10));
    setContentDraft(result.order.content_preparation || '');
    setTitleDraft(result.order.title || '');
    const nextProducts = Array.isArray(result.products) ? result.products : [];
    const drafts = {};
    nextProducts.forEach((p) => {
      drafts[String(p.id)] = String(p.units ?? 1);
    });
    setUnitsDrafts(drafts);
    setLoading(false);
  }, [token, manufacturingId, navigate]);

  useEffect(() => {
    if (!manufacturingId) {
      navigate('/Produccion');
      return;
    }
    loadOrder();
  }, [manufacturingId, loadOrder, navigate]);

  const loadCatalog = useCallback(async () => {
    const result = await list_manufacturing_catalog_products(token);
    if (result === 'INVALID') {
      navigate('/');
      return;
    }
    if (!result || result.ok !== true) {
      setCatalogOptions([]);
      return;
    }
    setCatalogOptions(
      (Array.isArray(result.products) ? result.products : []).map((p) => ({
        value: p.id,
        label: p.name || `Producto #${p.id}`,
        description: p.description || '',
      }))
    );
  }, [token, navigate]);

  useEffect(() => {
    if (!order) return;
    if (Number(order.opportunity_id) > 0) return;
    if (!isAdmin) return;
    loadCatalog();
  }, [order?.opportunity_id, isAdmin, loadCatalog]);

  const serialsByProduct = useMemo(() => {
    const map = {};
    serials.forEach((s) => {
      const key = serialGroupKey(s);
      if (!key) return;
      if (!map[key]) map[key] = [];
      map[key].push(s);
    });
    return map;
  }, [serials]);

  const uploadContentImage = useCallback(
    async (file) => upload_manufacturing_content_image(token, manufacturingId, file),
    [token, manufacturingId]
  );

  const handleSaveFields = async () => {
    if (!isAdmin) return;
    setSavingFields(true);
    const result = await update_manufacturing_order(token, manufacturingId, {
      priority,
      expected_finishing_date: expectedDate,
    });
    setSavingFields(false);
    if (result === 'INVALID') {
      navigate('/');
      return;
    }
    if (!result || result.ok !== true) {
      window.alert('No se pudieron guardar los campos.');
      return;
    }
    await loadOrder();
  };

  const handleSaveTitle = async () => {
    if (!isAdmin) return;
    const title = titleDraft.trim();
    if (!title) {
      window.alert('El título no puede estar vacío.');
      return;
    }
    setSavingTitle(true);
    const result = await update_manufacturing_order(token, manufacturingId, { title });
    setSavingTitle(false);
    if (result === 'INVALID') {
      navigate('/');
      return;
    }
    if (!result || result.ok !== true) {
      window.alert('No se pudo guardar el título.');
      return;
    }
    setEditingTitle(false);
    await loadOrder();
  };

  const handleAddCatalogProduct = async () => {
    if (!isAdmin || addingProduct) return;
    const productId = selectedCatalogProduct?.value;
    const units = parseInt(newProductUnits, 10);
    if (!productId || !Number.isFinite(units) || units < 1) {
      window.alert('Selecciona un producto y unidades válidas.');
      return;
    }
    setAddingProduct(true);
    const result = await add_manufacturing_product(token, manufacturingId, productId, units);
    setAddingProduct(false);
    if (result === 'INVALID') {
      navigate('/');
      return;
    }
    if (!result || result.ok !== true) {
      window.alert('No se pudo agregar el producto.');
      return;
    }
    setSelectedCatalogProduct(null);
    setNewProductUnits('1');
    await loadOrder();
  };

  const handleSaveProductUnits = async (prod) => {
    if (!isAdmin || prod.source !== 'manual') return;
    const key = String(prod.id);
    const units = parseInt(unitsDrafts[key], 10);
    if (!Number.isFinite(units) || units < 1) {
      window.alert('Las unidades deben ser al menos 1.');
      return;
    }
    setBusyProductLine(`units-${key}`);
    const result = await update_manufacturing_product(token, prod.id, { units });
    setBusyProductLine(null);
    if (result === 'INVALID') {
      navigate('/');
      return;
    }
    if (!result || result.ok !== true) {
      window.alert('No se pudieron actualizar las unidades.');
      return;
    }
    await loadOrder();
  };

  const handleDeleteProductLine = async (prod) => {
    if (!isAdmin || prod.source !== 'manual') return;
    const assigned = (serialsByProduct[String(prod.id)] || []).length;
    const msg = assigned > 0
      ? `Este producto tiene ${assigned} serial${assigned === 1 ? '' : 'es'}. ¿Eliminarlo de todos modos?`
      : '¿Eliminar este producto de la orden?';
    if (!window.confirm(msg)) return;
    setBusyProductLine(`del-${prod.id}`);
    const result = await delete_manufacturing_product(token, prod.id);
    setBusyProductLine(null);
    if (result === 'INVALID') {
      navigate('/');
      return;
    }
    if (!result || result.ok !== true) {
      window.alert('No se pudo eliminar el producto.');
      return;
    }
    await loadOrder();
  };

  const handleSaveDescription = async (html) => {
    if (!isAdmin) return;
    setSavingDescription(true);
    const prepared = prepareTaskDescriptionHtmlForSave(html || descriptionDraft);
    const result = await update_manufacturing_order(token, manufacturingId, {
      description: prepared,
    });
    setSavingDescription(false);
    if (result === 'INVALID') {
      navigate('/');
      return;
    }
    if (!result || result.ok !== true) {
      window.alert('No se pudo guardar la descripción.');
      return;
    }
    setEditingDescription(false);
    await loadOrder();
  };

  const handleSaveContent = async (html) => {
    if (!isAdmin) return;
    setSavingContent(true);
    const prepared = prepareTaskDescriptionHtmlForSave(html || contentDraft);
    const result = await update_manufacturing_order(token, manufacturingId, {
      content_preparation: prepared,
    });
    setSavingContent(false);
    if (result === 'INVALID') {
      navigate('/');
      return;
    }
    if (!result || result.ok !== true) {
      window.alert('No se pudo guardar la preparación de contenido.');
      return;
    }
    setEditingContent(false);
    await loadOrder();
  };

  const applyStatusChange = async (statusIndex, extra = {}) => {
    const result = await change_manufacturing_status(token, manufacturingId, statusIndex, extra);
    if (result === 'INVALID') {
      navigate('/');
      return { ok: false };
    }
    if (!result || result.ok !== true) {
      const isFacturaField = extra.field === 'factura' || extra.factura != null;
      let msg =
        result?.error === 'order_cancelled' ? 'Esta orden está anulada.'
        : (result?.error === 'invalid_factura' || result?.error === 'invalid_guia')
          ? (isFacturaField
            ? 'Número de factura inválido (alfanumérico, máx. 200).'
            : 'Número de guía inválido (alfanumérico, máx. 200).')
        : result?.error === 'invalid_warranty_date' ? 'Indica una fecha de garantía válida.'
        : result?.error === 'status_type_missing' ? 'No se encontró el estado Despachado en el sistema.'
        : 'No se pudo cambiar el estado.';
      if (result?.error === 'status_type_missing') {
        const avail = Array.isArray(result.available) ? result.available.filter(Boolean) : [];
        const missing = Array.isArray(result.missing) ? result.missing.filter(Boolean) : [];
        if (missing.length) msg += `\nFaltan: ${missing.join(', ')}.`;
        if (avail.length) msg += `\nEstados en BD: ${avail.join(', ')}.`;
        else msg += '\n(La consulta a status_type no devolvió nombres; revisa BD/permisos.)';
      }
      window.alert(msg);
      return { ok: false };
    }
    await loadOrder();
    return { ok: true, result };
  };

  const handleStatusChange = async (statusIndex) => {
    if (!isAdmin) return;
    if (Number(order?.status) === 5) {
      window.alert('Esta orden está anulada y no se puede cambiar de estado.');
      return;
    }
    if (statusIndex === 3) {
      setStatusFieldValue(order?.guia ? String(order.guia) : '');
      setStatusWarrantyDate('');
      setStatusFieldError('');
      setStatusWarrantyError('');
      setStatusModal({ status: 3, field: 'guia' });
      return;
    }
    if (statusIndex === 4) {
      setStatusFieldValue(order?.factura ? String(order.factura) : '');
      setStatusWarrantyDate('');
      setStatusFieldError('');
      setStatusWarrantyError('');
      setStatusModal({ status: 4, field: 'factura' });
      return;
    }
    await applyStatusChange(statusIndex);
  };

  const validateAlphanumeric = (value) => {
    const v = String(value || '').trim();
    if (!v) return 'Este campo es obligatorio.';
    if (v.length > 200) return 'Máximo 200 caracteres.';
    const ok = /^[\p{L}\p{N}][\p{L}\p{N}\s\-_.\/#,:+]*$/u.test(v)
      || /^[A-Za-z0-9][A-Za-z0-9\s\-_.\/#,:+]*$/.test(v);
    if (!ok) {
      return 'Solo se permiten letras, números y - _ / . # , : +';
    }
    return '';
  };

  const dispatchableSerialCount = useMemo(() => {
    if (!statusModal || statusModal.field !== 'guia') return 0;
    let count = 0;
    products.forEach((prod) => {
      const productId = Number(prod.product_id);
      if (productId < 1 || productId > 5) return;
      count += (serialsByProduct[String(prod.id)] || []).length;
    });
    return count;
  }, [statusModal, products, serialsByProduct]);

  const handleConfirmStatusModal = async () => {
    if (!statusModal || statusSaving) return;
    const err = validateAlphanumeric(statusFieldValue);
    if (err) {
      setStatusFieldError(err);
      return;
    }
    if (statusModal.field === 'guia') {
      if (!statusWarrantyDate || !/^\d{4}-\d{2}-\d{2}$/.test(statusWarrantyDate)) {
        setStatusWarrantyError('Indica la fecha de garantía.');
        return;
      }
    }
    setStatusSaving(true);
    const extra = statusModal.field === 'factura'
      ? { field: 'factura', factura: statusFieldValue.trim() }
      : {
          field: 'guia',
          guia: statusFieldValue.trim(),
          fecha_warranty: statusWarrantyDate,
        };
    const { ok, result } = await applyStatusChange(statusModal.status, extra);
    setStatusSaving(false);
    if (ok) {
      if (statusModal.field === 'guia' && result?.dispatch) {
        const d = result.dispatch;
        if (d.skipped && d.reason === 'status_type_missing') {
          const avail = Array.isArray(d.available) ? d.available.filter(Boolean) : [];
          const missing = Array.isArray(d.missing) ? d.missing.filter(Boolean) : [];
          const parts = [
            'Orden marcada como Despachado (guía y garantía guardadas).',
            'No se actualizaron los dispositivos: no se resolvió el estado Despachado en status_type.',
          ];
          if (missing.length) parts.push(`Faltan: ${missing.join(', ')}.`);
          if (avail.length) parts.push(`Estados en BD: ${avail.join(', ')}.`);
          else parts.push('(La consulta a status_type no devolvió nombres.)');
          window.alert(parts.join('\n'));
        } else {
          const parts = [
            `Orden marcada como Despachado.`,
            `Dispositivos actualizados a Despachado: ${d.updated_count || 0} de ${d.eligible || 0}.`,
          ];
          if ((d.already_despachado || []).length) {
            parts.push(`Ya estaban Despachado (se actualizó garantía): ${(d.already_despachado || []).length}.`);
          }
          if ((d.not_found || []).length) {
            parts.push(`No encontrados en serialization: ${(d.not_found || []).length}.`);
          }
          if ((d.failed || []).length) {
            parts.push(`Fallidos: ${(d.failed || []).length}.`);
          }
          window.alert(parts.join('\n'));
        }
      }
      setStatusModal(null);
      setStatusFieldValue('');
      setStatusWarrantyDate('');
      setStatusFieldError('');
      setStatusWarrantyError('');
    }
  };

  const handleCancelOrder = async () => {
    if (!isAdmin || Number(order?.status) === 5) return;
    if (!window.confirm('¿Cancelar esta orden de manufactura? Quedará en estado Anulado.')) return;
    await applyStatusChange(5);
  };

  const handleSubmitComment = async (htmlFromEditor) => {
    if (!isAdmin || submittingComment) return;
    const prepared = prepareTaskDescriptionHtmlForSave(htmlFromEditor ?? commentDraft);
    if (!prepared || !prepared.replace(/<[^>]+>/g, '').trim()) {
      window.alert('Escribe un comentario.');
      return;
    }
    setSubmittingComment(true);
    const result = await add_manufacturing_comment(token, manufacturingId, prepared);
    setSubmittingComment(false);
    if (result === 'INVALID') {
      navigate('/');
      return;
    }
    if (!result || result.ok !== true) {
      window.alert('No se pudo agregar el comentario.');
      return;
    }
    setCommentDraft('');
    setCommentKey((k) => k + 1);
    await loadOrder();
  };

  const handleUploadFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !isAdmin) return;
    const result = await upload_manufacturing_file(token, manufacturingId, file);
    if (result === 'INVALID') {
      navigate('/');
      return;
    }
    if (!result || result.ok !== true) {
      window.alert('No se pudo subir el archivo.');
      return;
    }
    await loadOrder();
  };

  const handleDeleteFile = async (fileId, e) => {
    e?.stopPropagation?.();
    if (!isAdmin) return;
    if (!window.confirm('¿Eliminar este archivo?')) return;
    const result = await delete_manufacturing_file(token, fileId);
    if (result === 'INVALID') {
      navigate('/');
      return;
    }
    if (!result || result.ok !== true) {
      window.alert('No se pudo eliminar el archivo.');
      return;
    }
    await loadOrder();
  };

  const handleAddSerial = async (prod) => {
    if (!isAdmin || !prod) return;
    const key = String(prod.id);
    const value = (serialDrafts[key] || '').trim();
    if (!value) return;
    setBusySerial(`add-${key}`);
    const result = await add_manufacturing_serial(
      token,
      manufacturingId,
      productLineRef(prod),
      value
    );
    setBusySerial(null);
    if (result === 'INVALID') {
      navigate('/');
      return;
    }
    if (!result || result.ok !== true) {
      window.alert(result?.error === 'db_error'
        ? 'No se pudo guardar el serial (¿coincide el nombre de columna en BD?).'
        : 'No se pudo agregar el serial.');
      return;
    }
    setSerialDrafts((prev) => ({ ...prev, [key]: '' }));
    await loadOrder();
  };

  const handleDeleteSerial = async (serialId) => {
    if (!isAdmin) return;
    setBusySerial(`del-${serialId}`);
    const result = await delete_manufacturing_serial(token, serialId);
    setBusySerial(null);
    if (result === 'INVALID') {
      navigate('/');
      return;
    }
    if (!result || result.ok !== true) {
      window.alert('No se pudo eliminar el serial.');
      return;
    }
    await loadOrder();
  };

  const openSerialUpload = (prod) => {
    setSerialUploadProduct(prod);
    setSerialParseResult(null);
    setSerialUploadFileName('');
  };

  const closeSerialUploadModal = () => {
    if (serialUploading) return;
    setSerialUploadProduct(null);
    setSerialParseResult(null);
    setSerialUploadFileName('');
    if (serialFileInputRef.current) serialFileInputRef.current.value = '';
  };

  const handleSerialFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !serialUploadProduct) return;

    const name = file.name || 'archivo';
    const lower = name.toLowerCase();
    if (!lower.endsWith('.txt') && !lower.endsWith('.csv')) {
      setSerialUploadFileName(name);
      setSerialParseResult({
        ok: false,
        valid: [],
        errors: [{ line: 0, serial: name, message: 'Usa un archivo .txt o .csv de texto plano.' }],
        warnings: [],
        totalLines: 0,
      });
      return;
    }

    try {
      const text = await file.text();
      const existing = (serialsByProduct[String(serialUploadProduct.id)] || []).map((s) => s.serial);
      const parsed = parseManufacturingSerialFile(text, existing);
      setSerialUploadFileName(name);
      setSerialParseResult(parsed);
    } catch (err) {
      setSerialUploadFileName(name);
      setSerialParseResult({
        ok: false,
        valid: [],
        errors: [{ line: 0, serial: name, message: 'No se pudo leer el archivo.' }],
        warnings: [],
        totalLines: 0,
      });
    }
  };

  const handleConfirmSerialUpload = async () => {
    if (!isAdmin || !serialUploadProduct || !serialParseResult?.ok || serialUploading) return;
    const list = serialParseResult.valid || [];
    if (list.length === 0) return;

    setSerialUploading(true);
    const result = await add_manufacturing_serials_bulk(
      token,
      manufacturingId,
      productLineRef(serialUploadProduct),
      list
    );
    setSerialUploading(false);

    if (result === 'INVALID') {
      navigate('/');
      return;
    }
    if (!result || result.ok !== true) {
      window.alert('No se pudieron guardar los seriales.');
      return;
    }

    const inserted = result.inserted_count ?? (result.inserted || []).length;
    const skipped = (result.skipped || []).length;
    const failed = (result.failed || []).length;
    let msg = `Se agregaron ${inserted} serial${inserted === 1 ? '' : 'es'}.`;
    if (skipped) msg += ` ${skipped} omitido${skipped === 1 ? '' : 's'}.`;
    if (failed) msg += ` ${failed} con error.`;
    window.alert(msg);

    closeSerialUploadModal();
    await loadOrder();
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="container py-4">
        <Button variant="link" className="px-0 mb-3" onClick={() => navigate('/Produccion')}>
          <ArrowLeft size={16} className="me-1" /> Volver
        </Button>
        <div className="alert alert-warning">Orden no encontrada.</div>
      </div>
    );
  }

  const isCancelled = Number(order.status) === 5;
  const hasOpportunity = Number(order.opportunity_id) > 0;
  const orderHeading = hasOpportunity
    ? (order.opportunity_name || `Oportunidad #${order.opportunity_id}`)
    : (order.title || `Orden #${order.id}`);

  return (
    <div className="produccion-detail container-fluid py-3">
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-2">
        <div>
          <Button variant="link" className="px-0 mb-1" onClick={() => navigate('/Produccion')}>
            <ArrowLeft size={16} className="me-1" /> Órdenes de producción
          </Button>
          <h3 className="mb-1">
            Orden #{order.id}
            <span className="text-muted fw-normal ms-2 fs-5">
              {orderHeading}
            </span>
            {isCancelled && (
              <Badge bg="danger" className="ms-2 align-middle">Anulado</Badge>
            )}
          </h3>
          <div className="text-muted small d-flex flex-wrap align-items-center gap-2">
            <span>Canal: {order.channel_name || '—'}</span>
            {hasOpportunity ? (
              <button
                type="button"
                className="btn btn-link btn-sm p-0 align-baseline"
                style={{ fontSize: 'inherit', verticalAlign: 'baseline' }}
                onClick={() => navigate(`/opportunitypage?id=${order.opportunity_id}`)}
              >
                Opp ID: {order.opportunity_id}
              </button>
            ) : (
              <Badge bg="light" text="dark">Sin oportunidad</Badge>
            )}
          </div>
          {!hasOpportunity && isAdmin && !isCancelled && (
            <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
              {editingTitle ? (
                <>
                  <Form.Control
                    size="sm"
                    style={{ maxWidth: 360 }}
                    value={titleDraft}
                    maxLength={255}
                    onChange={(e) => setTitleDraft(e.target.value)}
                    disabled={savingTitle}
                  />
                  <Button size="sm" variant="primary" disabled={savingTitle} onClick={handleSaveTitle}>
                    {savingTitle ? <Spinner size="sm" animation="border" /> : <><Save size={14} className="me-1" /> Guardar</>}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={savingTitle}
                    onClick={() => {
                      setEditingTitle(false);
                      setTitleDraft(order.title || '');
                    }}
                  >
                    <X size={14} className="me-1" /> Cancelar
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => {
                    setTitleDraft(order.title || '');
                    setEditingTitle(true);
                  }}
                >
                  <Edit2 size={14} className="me-1" /> Editar título
                </Button>
              )}
            </div>
          )}
        </div>
        {isAdmin && !isCancelled && (
          <Button variant="outline-danger" size="sm" onClick={handleCancelOrder}>
            Cancelar orden
          </Button>
        )}
      </div>

      {isCancelled && (
        <div className="alert alert-danger py-2">
          Esta orden está <strong>anulada</strong>. No se pueden cambiar estados ni editar campos.
        </div>
      )}

      <ManufacturingStatusBar
        currentStep={Number(order.status) || 0}
        cancelled={isCancelled}
        canEdit={isAdmin && !isCancelled}
        onStepClick={handleStatusChange}
      />

      <Row className="g-3 mb-3">
        <Col md={8}>
          <div className="mfg-card">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">Descripción</h5>
              {isAdmin && !isCancelled && !editingDescription && (
                <Button size="sm" variant="outline-secondary" onClick={() => {
                  setDescriptionDraft(order.description || '');
                  setEditingDescription(true);
                }}>
                  <Edit2 size={14} className="me-1" /> Editar
                </Button>
              )}
              {isAdmin && !isCancelled && editingDescription && (
                <Button size="sm" variant="outline-secondary" onClick={() => {
                  setEditingDescription(false);
                  setDescriptionDraft(order.description || '');
                }}>
                  <X size={14} className="me-1" /> Cancelar
                </Button>
              )}
            </div>
            {editingDescription ? (
              <TaskDescriptionEditor
                value={descriptionDraft}
                onChange={setDescriptionDraft}
                onSave={handleSaveDescription}
                onCancel={() => {
                  setEditingDescription(false);
                  setDescriptionDraft(order.description || '');
                }}
                saving={savingDescription}
                token={token}
                taskId={manufacturingId}
                uploadImageFn={uploadContentImage}
                editorKey={`mfg-description-${manufacturingId}`}
                placeholder="Descripción de la orden… Puedes pegar o insertar imágenes."
              />
            ) : (
              order.description
                ? <TaskDescriptionView html={order.description} />
                : <div className="text-muted small">Sin descripción.</div>
            )}
            <Row className="g-2 mt-3 align-items-end">
              <Col sm={5}>
                <Form.Label className="small mb-1">Prioridad</Form.Label>
                <Form.Select
                  value={priority}
                  disabled={!isAdmin || isCancelled}
                  onChange={(e) => setPriority(Number(e.target.value))}
                >
                  <option value={1}>1 — Urgente</option>
                  <option value={2}>2 — Normal</option>
                  <option value={3}>3 — Baja</option>
                </Form.Select>
              </Col>
              <Col sm={5}>
                <Form.Label className="small mb-1">Fecha esperada de finalización</Form.Label>
                <Form.Control
                  type="date"
                  value={expectedDate}
                  disabled={!isAdmin || isCancelled}
                  onChange={(e) => setExpectedDate(e.target.value)}
                />
              </Col>
              {isAdmin && !isCancelled && (
                <Col sm={2}>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    className="w-100"
                    disabled={savingFields}
                    onClick={handleSaveFields}
                  >
                    {savingFields
                      ? <Spinner size="sm" animation="border" />
                      : <><Save size={14} className="me-1" /> Guardar</>}
                  </Button>
                </Col>
              )}
            </Row>
          </div>
        </Col>
        <Col md={4}>
          <div className="mfg-card h-100">
            <h5 className="mb-2">Resumen</h5>
            <div className="small mb-1">
              <span className="text-muted">Prioridad:</span>{' '}
              {MANUFACTURING_PRIORITY_LABELS[order.priority] || order.priority}
            </div>
            <div className="small mb-1">
              <span className="text-muted">Creada:</span>{' '}
              {order.creation_date ? String(order.creation_date).slice(0, 16) : '—'}
            </div>
            <div className="small mb-1">
              <span className="text-muted">Finalizada:</span>{' '}
              {order.finished_date && order.finished_date !== '0000-00-00 00:00:00'
                ? String(order.finished_date).slice(0, 16)
                : '—'}
            </div>
            <div className="small mb-1">
              <span className="text-muted">Factura:</span>{' '}
              {order.factura ? String(order.factura) : '—'}
            </div>
            <div className="small">
              <span className="text-muted">Guía:</span>{' '}
              {order.guia ? String(order.guia) : '—'}
            </div>
          </div>
        </Col>
      </Row>

      <div className="mfg-card mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">Preparación de contenido</h5>
          {isAdmin && !isCancelled && !editingContent && (
            <Button size="sm" variant="outline-secondary" onClick={() => {
              setContentDraft(order.content_preparation || '');
              setEditingContent(true);
            }}>
              <Edit2 size={14} className="me-1" /> Editar
            </Button>
          )}
          {isAdmin && !isCancelled && editingContent && (
            <Button size="sm" variant="outline-secondary" onClick={() => {
              setEditingContent(false);
              setContentDraft(order.content_preparation || '');
            }}>
              <X size={14} className="me-1" /> Cancelar
            </Button>
          )}
        </div>
        {editingContent ? (
          <TaskDescriptionEditor
            value={contentDraft}
            onChange={setContentDraft}
            onSave={handleSaveContent}
            onCancel={() => {
              setEditingContent(false);
              setContentDraft(order.content_preparation || '');
            }}
            saving={savingContent}
            token={token}
            taskId={manufacturingId}
            uploadImageFn={uploadContentImage}
            editorKey={`mfg-content-${manufacturingId}`}
            placeholder="Preparación de contenido… Puedes pegar o insertar imágenes."
          />
        ) : (
          order.content_preparation
            ? <TaskDescriptionView html={order.content_preparation} />
            : <div className="text-muted small">Sin preparación de contenido.</div>
        )}
      </div>

      <div className="mfg-card mb-3">
        <div className="d-flex align-items-center gap-2 mb-2">
          <h5 className="me-2 mb-0 fw-normal">Archivos:</h5>
          {isAdmin && !isCancelled && (
            <>
              <Button
                variant="outline-dark"
                size="sm"
                className="square-btn"
                onClick={() => fileInputRef.current?.click()}
              >
                +
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                className="d-none"
                onChange={handleUploadFile}
              />
            </>
          )}
        </div>
        <div className="d-flex flex-wrap gap-2">
          {files.length === 0 ? (
            <span className="text-muted small">Sin archivos adjuntos.</span>
          ) : (
            files.map((f) => (
              <Badge
                key={f.id}
                bg="secondary"
                pill
                className="chip d-inline-flex align-items-center gap-2 px-3 py-2"
                style={{ cursor: 'pointer' }}
                onClick={() => window.open(manufacturingFileUrl(manufacturingId, f.filename), '_blank')}
              >
                {f.original_name || f.filename}
                {isAdmin && !isCancelled && (
                  <button
                    type="button"
                    className="btn-close btn-close-white btn-close-sm ms-1"
                    aria-label="Eliminar"
                    onClick={(e) => handleDeleteFile(f.id, e)}
                  />
                )}
              </Badge>
            ))
          )}
        </div>
      </div>

      <div className="mfg-card mb-3">
        <h5 className="mb-3">Productos por alistar</h5>
        {!hasOpportunity && isAdmin && !isCancelled && (
          <div className="mfg-add-product-row mb-3">
            <div className="mfg-add-product-select">
              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                options={catalogOptions}
                isLoading={catalogOptions.length === 0}
                value={selectedCatalogProduct}
                onChange={setSelectedCatalogProduct}
                placeholder="Producto…"
                isSearchable
                isClearable
                menuPortalTarget={document.body}
                styles={{
                  container: (provided) => ({ ...provided, flex: 1, minWidth: 180 }),
                  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
                }}
              />
            </div>
            <Form.Control
              size="sm"
              type="number"
              min={1}
              style={{ width: 90 }}
              value={newProductUnits}
              onChange={(e) => setNewProductUnits(e.target.value)}
              placeholder="Uds"
            />
            <Button
              size="sm"
              variant="primary"
              disabled={addingProduct || !selectedCatalogProduct}
              onClick={handleAddCatalogProduct}
            >
              {addingProduct ? <Spinner size="sm" animation="border" /> : <><Plus size={14} className="me-1" /> Agregar</>}
            </Button>
          </div>
        )}
        {products.length === 0 ? (
          <div className="text-muted small">
            {hasOpportunity
              ? 'Esta oportunidad no tiene productos.'
              : 'No hay productos. Agrega productos del catálogo.'}
          </div>
        ) : (
          products.map((prod) => {
            const prodSerials = serialsByProduct[String(prod.id)] || [];
            const draftKey = String(prod.id);
            const filterText = (serialDrafts[draftKey] || '').trim().toLowerCase();
            const filteredSerials = filterText
              ? prodSerials.filter((s) => String(s.serial || '').toLowerCase().includes(filterText))
              : prodSerials;
            const required = Math.max(0, Number(prod.units) || 0);
            const assigned = prodSerials.length;
            const remaining = Math.max(0, required - assigned);
            const isComplete = required > 0 && assigned >= required;
            const isOver = assigned > required && required > 0;
            const progressPct = required > 0
              ? Math.min(100, Math.round((assigned / required) * 100))
              : (assigned > 0 ? 100 : 0);
            const progressClass = isOver
              ? 'mfg-units-progress-bar is-over'
              : isComplete
                ? 'mfg-units-progress-bar is-complete'
                : assigned > 0
                  ? 'mfg-units-progress-bar is-partial'
                  : 'mfg-units-progress-bar';
            const canAddSerial = isAdmin && !isCancelled;
            const canEditLine = isAdmin && !isCancelled && prod.source === 'manual';
            const serialPlaceholder = canAddSerial
              ? (remaining > 0
                ? `Buscar o agregar serial (${remaining} pendiente${remaining === 1 ? '' : 's'})…`
                : 'Buscar o agregar serial…')
              : 'Buscar serial…';
            return (
              <div
                key={prod.id}
                className={`mfg-product-block mb-3${isComplete && !isOver ? ' is-complete' : ''}${isOver ? ' is-over' : ''}`}
              >
                <div className="mfg-product-header mb-2">
                  <div className="mfg-product-info">
                    <div className="fw-semibold">{prod.name}</div>
                    {prod.prod_description ? (
                      <div className="text-muted small">{prod.prod_description}</div>
                    ) : null}
                    {canEditLine && (
                      <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                        <Form.Label className="small mb-0 text-muted">Unidades</Form.Label>
                        <Form.Control
                          size="sm"
                          type="number"
                          min={1}
                          style={{ width: 80 }}
                          value={unitsDrafts[draftKey] ?? String(prod.units ?? 1)}
                          onChange={(e) =>
                            setUnitsDrafts((prev) => ({ ...prev, [draftKey]: e.target.value }))
                          }
                          disabled={busyProductLine === `units-${draftKey}`}
                        />
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          disabled={
                            busyProductLine === `units-${draftKey}`
                            || String(unitsDrafts[draftKey] ?? '') === String(prod.units ?? '')
                          }
                          onClick={() => handleSaveProductUnits(prod)}
                        >
                          {busyProductLine === `units-${draftKey}`
                            ? <Spinner size="sm" animation="border" />
                            : 'Guardar uds'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          disabled={busyProductLine === `del-${prod.id}`}
                          onClick={() => handleDeleteProductLine(prod)}
                        >
                          {busyProductLine === `del-${prod.id}`
                            ? <Spinner size="sm" animation="border" />
                            : 'Quitar'}
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="mfg-serial-input-slot">
                    <Form.Control
                      size="sm"
                      placeholder={serialPlaceholder}
                      value={serialDrafts[draftKey] || ''}
                      onChange={(e) =>
                        setSerialDrafts((prev) => ({ ...prev, [draftKey]: e.target.value }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (canAddSerial) handleAddSerial(prod);
                        }
                      }}
                    />
                    {canAddSerial && (
                      <>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          disabled={busySerial === `add-${draftKey}`}
                          onClick={() => handleAddSerial(prod)}
                        >
                          Agregar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          title="Subir archivo de seriales"
                          onClick={() => openSerialUpload(prod)}
                        >
                          <Upload size={14} />
                        </Button>
                      </>
                    )}
                  </div>
                  <div className="mfg-units-summary">
                    <span className={`mfg-units-count${isComplete && !isOver ? ' is-complete' : ''}${isOver ? ' is-over' : ''}${!isComplete && !isOver && assigned > 0 ? ' is-partial' : ''}`}>
                      <strong>{assigned}</strong>
                      <span className="mfg-units-sep">/</span>
                      <strong>{required}</strong>
                    </span>
                    <span className="mfg-units-label text-muted small">seriales / uds</span>
                    <div className="mfg-units-progress" aria-hidden="true">
                      <div className={progressClass} style={{ width: `${progressPct}%` }} />
                    </div>
                    {isComplete && !isOver && (
                      <Badge bg="success" pill>Completo</Badge>
                    )}
                    {!isComplete && remaining > 0 && (
                      <Badge bg="warning" text="dark" pill>
                        Faltan {remaining}
                      </Badge>
                    )}
                    {isOver && (
                      <Badge bg="danger" pill>
                        +{assigned - required} de más
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-1">
                  {filteredSerials.length === 0 ? (
                    <span className="text-muted small">Sin seriales.</span>
                  ) : (
                    filteredSerials.map((s) => (
                      <Badge
                        key={s.id}
                        bg="light"
                        text="dark"
                        className="mfg-serial-badge"
                      >
                        {s.serial}
                        {canAddSerial && (
                          <button
                            type="button"
                            className="btn-close btn-close-sm ms-1"
                            aria-label="Eliminar"
                            disabled={busySerial === `del-${s.id}`}
                            onClick={() => handleDeleteSerial(s.id)}
                          />
                        )}
                      </Badge>
                    ))
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="mfg-card mb-4">
        <h5 className="mb-3">Novedades</h5>
        {isAdmin && !isCancelled && (
          <div className="mb-3">
            <StrategyCommentComposer
              value={commentDraft}
              onChange={setCommentDraft}
              onSubmit={handleSubmitComment}
              disabled={submittingComment}
              token={token}
              uploadImageFn={uploadContentImage}
              editorKey={`mfg-comment-${commentKey}`}
              placeholder="Escribe un comentario…"
            />
          </div>
        )}
        <div className="mfg-comments-list">
          {comments.length === 0 ? (
            <div className="text-muted small">Sin comentarios aún.</div>
          ) : (
            comments.map((row) => (
              <div key={row.id} className="mfg-comment-item">
                <div className="mfg-comment-main">
                  <CommentBody text={row.commets} />
                </div>
                <div className="mfg-comment-aside">
                  <UserAvatar
                    usr_id={row.creator_usr_id}
                    usr_name={row.creator_name || usr_name || 'Usuario'}
                  />
                  <span className="mfg-comment-date">{formatCommentDate(row.date)}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <input
        ref={serialFileInputRef}
        type="file"
        accept=".txt,.csv,text/plain"
        className="d-none"
        onChange={handleSerialFileSelected}
      />

      <Modal
        show={!!serialUploadProduct}
        onHide={closeSerialUploadModal}
        centered
        dialogClassName="mfg-status-modal mfg-serial-upload-modal"
        contentClassName="mfg-status-modal__content"
      >
        <Modal.Header closeButton={!serialUploading} className="mfg-status-modal__header">
          <Modal.Title className="mfg-status-modal__title">
            Subir seriales
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="mfg-status-modal__body">
          <p className="mfg-status-modal__hint mb-2">
            Producto: <strong>{serialUploadProduct?.name}</strong>
          </p>
          <p className="mfg-status-modal__hint">
            Archivo plano (.txt o .csv) con <strong>un serial por línea</strong>.
            Se validará el formato antes de guardar.
          </p>

          <div className="mfg-serial-upload-actions mb-3">
            <Button
              variant="outline-primary"
              size="sm"
              disabled={serialUploading}
              onClick={() => serialFileInputRef.current?.click()}
            >
              <Upload size={14} className="me-1" />
              {serialParseResult ? 'Elegir otro archivo' : 'Seleccionar archivo'}
            </Button>
            {serialUploadFileName ? (
              <span className="mfg-serial-upload-filename">{serialUploadFileName}</span>
            ) : null}
          </div>

          {!serialParseResult && (
            <div className="text-muted small">
              Aún no hay archivo. Elige uno para revisar el parseo.
            </div>
          )}

          {serialParseResult && serialParseResult.errors?.length > 0 && (
            <div className="mfg-serial-upload-errors mb-3">
              <div className="fw-semibold text-danger mb-2">
                Errores de formato ({serialParseResult.errors.length})
              </div>
              <ul className="mfg-serial-upload-list">
                {serialParseResult.errors.slice(0, 40).map((err, idx) => (
                  <li key={`err-${idx}`}>
                    {err.line > 0 ? <>Línea {err.line}: </> : null}
                    <code>{err.serial}</code> — {err.message}
                  </li>
                ))}
              </ul>
              {serialParseResult.errors.length > 40 && (
                <div className="small text-muted">
                  …y {serialParseResult.errors.length - 40} más.
                </div>
              )}
              <div className="alert alert-warning py-2 mt-2 mb-0 small">
                Corrige el archivo y vuelve a seleccionarlo. No se puede subir mientras haya errores.
              </div>
            </div>
          )}

          {serialParseResult && serialParseResult.warnings?.length > 0 && (
            <div className="mfg-serial-upload-warnings mb-3">
              <div className="fw-semibold mb-2" style={{ color: '#a16207' }}>
                Advertencias ({serialParseResult.warnings.length})
              </div>
              <ul className="mfg-serial-upload-list">
                {serialParseResult.warnings.slice(0, 20).map((w, idx) => (
                  <li key={`warn-${idx}`}>
                    {w.line > 0 ? <>Línea {w.line}: </> : null}
                    <code>{w.serial}</code> — {w.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {serialParseResult && serialParseResult.errors?.length === 0 && (
            <div className={`mfg-serial-upload-summary${serialParseResult.valid.length ? ' is-ready' : ''}`}>
              {serialParseResult.valid.length > 0 ? (
                <>
                  <div className="fw-semibold mb-1">
                    Archivo parseado correctamente
                  </div>
                  <div className="mfg-serial-upload-count">
                    Se subirán <strong>{serialParseResult.valid.length}</strong> serial
                    {serialParseResult.valid.length === 1 ? '' : 'es'}.
                  </div>
                  <div className="small text-muted mt-2 mb-1">Vista previa:</div>
                  <div className="mfg-serial-upload-preview">
                    {serialParseResult.valid.slice(0, 12).map((s) => (
                      <Badge key={s} bg="info" pill className="chip px-2 py-1">
                        {s}
                      </Badge>
                    ))}
                    {serialParseResult.valid.length > 12 && (
                      <span className="small text-muted">
                        +{serialParseResult.valid.length - 12} más
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <div className="alert alert-secondary py-2 mb-0 small">
                  No hay seriales nuevos para subir (vacío o ya existentes).
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="mfg-status-modal__footer">
          <Button
            variant="outline-secondary"
            disabled={serialUploading}
            onClick={closeSerialUploadModal}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={
              serialUploading
              || !serialParseResult?.ok
              || !(serialParseResult?.valid?.length > 0)
            }
            onClick={handleConfirmSerialUpload}
          >
            {serialUploading
              ? <Spinner size="sm" animation="border" />
              : `Confirmar ${serialParseResult?.valid?.length || 0} serial${(serialParseResult?.valid?.length || 0) === 1 ? '' : 'es'}`}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!statusModal}
        onHide={() => !statusSaving && setStatusModal(null)}
        centered
        dialogClassName="mfg-status-modal"
        contentClassName="mfg-status-modal__content"
      >
        <Modal.Header closeButton={!statusSaving} className="mfg-status-modal__header">
          <Modal.Title className="mfg-status-modal__title">
            {statusModal?.field === 'factura' ? 'Número de factura' : 'Despachado'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="mfg-status-modal__body">
          <p className="mfg-status-modal__hint">
            {statusModal?.field === 'factura'
              ? 'Para marcar la orden como Facturado debes indicar el número de factura.'
              : 'Para marcar la orden como Despachado debes indicar el número de guía y la fecha de garantía.'}
          </p>

          {statusModal?.field === 'guia' && (
            <div className="alert alert-warning mfg-status-modal__warning py-2 mb-3">
              <strong>Atención:</strong> se cambiará el estado de todos los dispositivos
              asociados (productos tipo 1 a 5) a{' '}
              <strong>Despachado</strong>
              {dispatchableSerialCount > 0
                ? ` (${dispatchableSerialCount} serial${dispatchableSerialCount === 1 ? '' : 'es'} en esta orden).`
                : ' (no hay seriales de productos 1–5 en esta orden).'}
            </div>
          )}

          <Form.Group className="mfg-status-modal__field" controlId="mfg-status-field">
            <Form.Label className="mfg-status-modal__label">
              {statusModal?.field === 'factura' ? 'Factura' : 'Número de guía'}
            </Form.Label>
            <Form.Control
              type="text"
              inputMode="text"
              autoComplete="off"
              maxLength={200}
              autoFocus
              value={statusFieldValue}
              disabled={statusSaving}
              isInvalid={!!statusFieldError}
              placeholder={statusModal?.field === 'factura' ? 'Ej. EXE387' : 'Ej. GUIA-98765'}
              onChange={(e) => {
                setStatusFieldValue(e.target.value);
                if (statusFieldError) setStatusFieldError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && statusModal?.field === 'factura') {
                  e.preventDefault();
                  handleConfirmStatusModal();
                }
              }}
            />
            <Form.Control.Feedback type="invalid">{statusFieldError}</Form.Control.Feedback>
            <Form.Text muted className="mfg-status-modal__help">
              Alfanumérico, máximo 200 caracteres.
            </Form.Text>
          </Form.Group>

          {statusModal?.field === 'guia' && (
            <Form.Group className="mfg-status-modal__field mt-3" controlId="mfg-warranty-date">
              <Form.Label className="mfg-status-modal__label">Fecha de garantía</Form.Label>
              <Form.Control
                type="date"
                value={statusWarrantyDate}
                disabled={statusSaving}
                isInvalid={!!statusWarrantyError}
                onChange={(e) => {
                  setStatusWarrantyDate(e.target.value);
                  if (statusWarrantyError) setStatusWarrantyError('');
                }}
              />
              <Form.Control.Feedback type="invalid">{statusWarrantyError}</Form.Control.Feedback>
              <Form.Text muted className="mfg-status-modal__help">
                Se aplicará como garantía hasta en los dispositivos despachados.
              </Form.Text>
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer className="mfg-status-modal__footer">
          <Button
            variant="outline-secondary"
            disabled={statusSaving}
            onClick={() => setStatusModal(null)}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={statusSaving}
            onClick={handleConfirmStatusModal}
          >
            {statusSaving ? <Spinner size="sm" animation="border" /> : 'Confirmar cambio'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
